import type { ReactNode } from 'react';

import { InputAdornment, TextField } from '@mui/material';

import { FieldRow } from './FieldRow';

export interface NumberFieldProps {
  label: ReactNode;
  description?: ReactNode;
  value: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  /** Trailing unit label, e.g. "minutes". */
  suffix?: string;
  placeholder?: string;
  /** A range/cross-field validation failure (e.g. out of the contract's min/max) — renders the
   * input in its error state and shows `helperText` below it, independent of `description`. */
  error?: boolean;
  helperText?: ReactNode;
  /**
   * Explicit accessible name, for when `label` is rich content (e.g. a channel-scope chip/action
   * rendered alongside the text) rather than a plain string the control can read directly.
   */
  ariaLabel?: string;
}

/** Labelled nullable integer input laid out as a settings row. */
export function NumberField({
  label,
  description,
  value,
  onChange,
  disabled = false,
  min,
  max,
  step,
  suffix,
  placeholder,
  error = false,
  helperText,
  ariaLabel,
}: NumberFieldProps) {
  return (
    <FieldRow label={label} description={description}>
      <TextField
        size="small"
        type="number"
        value={value ?? ''}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        helperText={helperText}
        onChange={(event) => {
          const raw = event.target.value;
          onChange(raw === '' ? null : Number(raw));
        }}
        sx={{ maxWidth: 220 }}
        slotProps={{
          htmlInput: {
            min,
            max,
            step,
            'aria-label': ariaLabel ?? (typeof label === 'string' ? label : undefined),
          },
          input: suffix
            ? { endAdornment: <InputAdornment position="end">{suffix}</InputAdornment> }
            : undefined,
        }}
      />
    </FieldRow>
  );
}
